import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaRegEdit } from "react-icons/fa";
import { MdAdd, MdClose, MdCheck, MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiImage, FiHome } from "react-icons/fi";
import PanelSkeleton from "../PanelSkeleton";
import ProductImage from "../ProductImage";
import mediaUrl from "../../utils/mediaUrl";
import store from "../../redux/store";
import styles from "../../styles/categoriesPanel.module.css";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers },
  };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer-homes`;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const CustomerHomesPanel = () => {
  const [homes, setHomes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [reordering, setReordering] = useState(false);
  const [editId, setEditId]         = useState(null);
  const [caption, setCaption]       = useState("");
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const imgRef = useRef(null);

  const fetchHomes = async () => {
    try {
      const res = await axios.get(`${API}/dashboardFetch`, authConfig());
      setHomes(res.data?.data ?? []);
    } catch {
      toast.error(<span className="toast">فشل تحميل الصور</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHomes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setEditId(null);
    setCaption("");
    setImageFile(null);
    setImagePreview(null);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (home) => {
    resetForm();
    setEditId(home.id);
    setCaption(home.caption ?? "");
    setImagePreview(mediaUrl(home.imageUrl));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 5 ميجابايت</span>);
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editId && !imageFile) {
      toast.error(<span className="toast">اختر صورة أولاً</span>);
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption.trim());
      if (imageFile) fd.append("imageFile", imageFile);

      if (editId) {
        await axios.put(`${API}/${editId}`, fd, authConfig());
        toast.success(<span className="toast">تم تعديل الصورة</span>);
      } else {
        await axios.post(API, fd, authConfig());
        toast.success(<span className="toast">تم إضافة الصورة</span>);
      }
      closeForm();
      fetchHomes();
    } catch {
      toast.error(<span className="toast">فشلت العملية. حاول مرة أخرى</span>);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (home) => {
    confirmAlert({
      message: "حذف هذه الصورة ؟",
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(`${API}/${home.id}`, authConfig());
              toast.success(<span className="toast">تم حذف الصورة</span>);
              if (editId === home.id) closeForm();
              fetchHomes();
            } catch {
              toast.error(<span className="toast">فشل الحذف. حاول مرة أخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  const move = async (index, direction) => {
    const next = [...homes];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;

    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setHomes(next);

    setReordering(true);
    try {
      await axios.put(
        `${API}/reorder/batch`,
        { items: next.map((home, i) => ({ id: home.id, sortOrder: i })) },
        authConfig()
      );
    } catch {
      toast.error(<span className="toast">فشل حفظ الترتيب. حاول مرة أخرى</span>);
      fetchHomes();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiHome className={styles.panelTitleIcon} />
          في بيوت عملائنا
        </span>
        <button className={styles.addBtn} onClick={openAdd} disabled={showForm}>
          <MdAdd /> إضافة صورة
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit} encType="multipart/form-data">
          <div className={styles.formHead}>
            <span className={styles.formTitle}>{editId ? "تعديل الصورة" : "صورة جديدة"}</span>
            <button type="button" className={styles.formClose} onClick={closeForm}>
              <MdClose />
            </button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>الصورة *</label>
            {imagePreview ? (
              <div className={styles.imgPreviewWrap} style={{ width: "10rem", height: "7rem" }}>
                <ProductImage url={imagePreview} alt="preview" className={styles.imgPreview} />
                <button
                  type="button"
                  className={styles.imgRemoveBtn}
                  onClick={() => imgRef.current?.click()}
                  style={{ background: "rgba(155,119,61,.9)" }}
                  title="تغيير الصورة"
                >
                  <FaRegEdit />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.imgPickBtn}
                onClick={() => imgRef.current?.click()}
              >
                <FiImage className={styles.imgPickIcon} />
                اختيار صورة
              </button>
            )}
            <input
              type="file"
              accept="image/*"
              ref={imgRef}
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>تعليق (اختياري)</label>
            <input
              className={styles.formInput}
              placeholder="مثال: بيت عائلة أحمد - القدس"
              maxLength={100}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
            </button>
            <button type="button" className={styles.cancelFormBtn} onClick={closeForm}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* ── List ── */}
      {loading ? (
        <PanelSkeleton count={3} />
      ) : homes.length === 0 ? (
        <p className="warn">لا توجد صور بعد. أضف أول صورة من بيوت عملائك!</p>
      ) : (
        <div className={styles.list}>
          {homes.map((home, i) => (
            <div key={home.id} className={styles.row} style={{ animationDelay: `${i * 0.04}s` }}>

              <div className={styles.orderBtns}>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "up")}
                  disabled={i === 0 || reordering || deleting}
                  title="تحريك لأعلى"
                >
                  <MdKeyboardArrowUp />
                </button>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "down")}
                  disabled={i === homes.length - 1 || reordering || deleting}
                  title="تحريك لأسفل"
                >
                  <MdKeyboardArrowDown />
                </button>
              </div>

              <div className={styles.thumb} style={{ width: "4.2rem" }}>
                <ProductImage url={mediaUrl(home.imageUrl)} alt={home.caption ?? ""} className={styles.thumbImg} />
              </div>

              <span className={styles.catName}>{home.caption || "بدون تعليق"}</span>

              <span className={styles.orderBadge}>{i + 1}</span>

              <div className={styles.actions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(home)}
                  disabled={deleting}
                  title="تعديل"
                >
                  <FaRegEdit />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => !deleting && handleDelete(home)}
                  disabled={deleting}
                  title="حذف"
                >
                  {deleting ? <ImSpinner2 className="smallspinner" /> : <FaRegTrashAlt />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.hint}>
        تظهر هذه الصور في قسم "في بيوت عملائنا" بالصفحة الرئيسية بنفس الترتيب هنا.
      </p>
    </div>
  );
};

export default CustomerHomesPanel;
